import { Component, EventEmitter, inject, input, OnInit, Output, TemplateRef, viewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { RoomService } from '../../../services/room/room.service';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { Room } from '../../../../shared-api/room';
import { GenericTableComponent, TableColumn } from '../../core/generic-table/generic-table.component';
import { SearchbarComponent } from '../../../common/components/searchbar/searchbar.component';
import { ButtonComponent } from '../../../common/components/button/button.component';
import { catchError, from, of, tap } from 'rxjs';
import { MatrixService } from '../../../services/matrix/matrix.service';
import { DropdownComponent } from '../../../common/components/dropdown/dropdown.component';

export interface EndpointDialogRow {
  endpoint: string;
  action?: any;
  isMember?: boolean;
}

export interface RoomInfo {
  room: Room | null;
  name?: string;
  topic?: string;
  alias?: string;
  visibility?: boolean;
}

export interface EndpointDialogData {
  serviceName: string;
  rows: EndpointDialogRow[];
  roomInfo: RoomInfo;
}

@Component({
  selector: 'app-organisation-mxid-result-dialog',
  templateUrl: './sb-mxid-organisation-dialog.component.html',
  styleUrl: './sb-mxid-organisation-dialog.component.scss',
  imports: [SearchbarComponent, GenericTableComponent, ButtonComponent]
})
/**
 * Komponente für die Anzeige der MxIds einer Organisation
 *
 * BEGRÜNDUNG: Es gibt keine Afo
 */
export class SbMxidOrganisationDialogComponent implements OnInit {
  public readonly data = inject<EndpointDialogData>(MAT_DIALOG_DATA);
  readonly publicDropdownComponent = input<DropdownComponent>();
  readonly privateDropdownComponent = input<DropdownComponent>();
  readonly actionTemplate = viewChild.required<TemplateRef<any>>('actionTemplate');
  columns: TableColumn<EndpointDialogRow>[] = [];
  displayedColumns: TableColumn<EndpointDialogRow>[] = [];
  filteredRows: EndpointDialogRow[] = [];
  filterTerm = '';
  pageIndex = 0;
  pageSize = 5;
  totalItems = 0;
  showSpinner = false;
  isCreatingRoom = false;
  @Output() navigatedRoom = new EventEmitter<string>();
  private readonly matDialog = inject(MatDialog);
  private readonly roomService = inject(RoomService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly clientService = inject(MatrixService);
  private readonly dialogRef = inject(MatDialogRef<SbMxidOrganisationDialogComponent>);

  ngOnInit(): void {
    this.columns = [
      { key: 'endpoint', label: 'Matrix-ID', sortable: false },
      { key: 'action', label: 'Aktion', type: 'custom', template: this.actionTemplate() }
    ];
    this.displayedColumns = [...this.columns];

    this.checkMembershipStatus();
  }

  checkMembershipStatus(): void {
    this.data.rows.forEach(row => {
      if (this.data.roomInfo.room) {
        const room = this.clientService.client.getRoom(this.data.roomInfo.room.roomId);
        if (room) {
          row.isMember = room.getMembers().some(m => m.userId === row.endpoint);
        } else {
          row.isMember = false;
        }
      } else {
        row.isMember = false;
      }
    });
    this.applyFilter('');
  }

  applyFilter(searchTerm: string): void {
    this.filterTerm = searchTerm.toLowerCase();
    const filtered = this.data.rows.filter(row => row.endpoint.toLowerCase().includes(this.filterTerm));
    this.totalItems = filtered.length;
    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;
    this.filteredRows = filtered.slice(start, end);
  }

  onPageChange(event: { pageIndex: number; pageSize: number }): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.applyFilter(this.filterTerm);
  }

  invite(mxid: string): void {
    if (!this.data.roomInfo.room) return;

    from(this.roomService.inviteIntoRoom(this.data.roomInfo.room.roomId, [{ mxid }]))
      .pipe(
        tap(() => {
          this.snackbarService.success(`${mxid} wurde eingeladen.`);
          this.matDialog.closeAll();
        }),
        catchError(err => {
          console.error(err);
          this.snackbarService.error(`Einladung von ${mxid} fehlgeschlagen.`);
          this.matDialog.closeAll();
          return of(null);
        })
      )
      .subscribe();
  }

  close(): void {
    this.dialogRef.close();
  }

  createRoom(mxid: string): void {
    this.dialogRef.close({ mxid });
  }
}
